import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { 
  uploadAxisExcel, 
  getAxisDashboard, 
  updateAxisVoucher, 
  exportAxisMatched 
} from "../api/misApis";

/* =========================
   DATE FORMATTER
========================= */
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}-${mm}-${yy}`;
};

function AxisBankAutomation() {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const [matchedRows, setMatchedRows] = useState([]);
  const [unmatchedRows, setUnmatchedRows] = useState([]);

  /* =========================
     FETCH DATA
  ========================= */
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await getAxisDashboard();
      setMatchedRows(res.data.matched || []);
      setUnmatchedRows(res.data.unmatched || []);
    } catch (err) {
      console.error("Fetch error:", err);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to fetch dashboard data!',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  /* =========================
     ACTIONS
  ========================= */
  const handleUpload = async () => {
    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: 'No file selected',
        text: 'Please select an Excel file first!',
      });
      return;
    }
    try {
      setUploading(true);
      const res = await uploadAxisExcel(file);
      
      Swal.fire({
        icon: 'success',
        title: 'Uploaded!',
        text: res.data.message || "File uploaded successfully!",
        timer: 2000,
        showConfirmButton: false
      });

      setFile(null);
      document.getElementById("axisFileInput").value = ""; 
      fetchDashboard();
    } catch (err) {
      console.error("Upload error:", err);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: err.response?.data?.error || "Something went wrong during upload.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (matchedRows.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to export',
        text: 'No matched transactions available to export.',
      });
      return;
    }
    try {
      const res = await exportAxisMatched();
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Axis_ERP_Upload_${new Date().getTime()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: 'Matched data has been moved to the archive.',
      });

      fetchDashboard(); // Refresh to clear matched table
    } catch (err) {
      console.error("Export error:", err);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Could not export the data.',
      });
    }
  };

  const handleEditVoucher = async (transactionId, currentVoucher) => {
    const { value: newVoucher, isConfirmed } = await Swal.fire({
      title: 'Update Voucher Number',
      input: 'text',
      inputLabel: 'Enter the correct voucher number for this transaction',
      inputValue: currentVoucher || "",
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      buttonsStyling: false, 
      customClass: {
        confirmButton: 'px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition-colors mr-3',
        cancelButton: 'px-5 py-2.5 bg-red-500 text-white rounded-lg font-semibold shadow hover:bg-red-600 transition-colors',
        input: 'border border-gray-300 rounded-lg px-4 py-2 mt-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
      },
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return 'You need to write something!';
        }
      }
    });

    if (isConfirmed && newVoucher) {
      try {
        setLoading(true);
        const res = await updateAxisVoucher(transactionId, newVoucher.trim());
        
        Swal.fire({
          icon: res.data.message.includes('successfully matched') ? 'success' : 'info',
          title: 'Update Status',
          text: res.data.message,
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition-colors'
          }
        });

        fetchDashboard(); 
      } catch (err) {
        console.error("Update error:", err);
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: 'Failed to update voucher.',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'px-5 py-2.5 bg-red-600 text-white rounded-lg font-semibold shadow hover:bg-red-700 transition-colors'
          }
        });
      } finally {
        setLoading(false);
      }
    }
  };

  /* =========================
     HELPERS
  ========================= */
  // Columns Definition
  const matchedCols = useMemo(() => (matchedRows[0] ? Object.keys(matchedRows[0]).filter(k => k !== 'AxisTransactionId' && k !== 'Id') : []), [matchedRows]);
  const unmatchedCols = useMemo(() => (unmatchedRows[0] ? Object.keys(unmatchedRows[0]).filter(k => k !== 'Id') : []), [unmatchedRows]);

  /* =========================
     UI
  ========================= */
  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      {/* TOP BAR */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-white text-gray-600 rounded-full shadow hover:bg-gray-100 transition-colors"
            title="Go Back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Axis Bank Reconciliation</h1>
        </div>
      </div>

      {/* UPLOAD & EXPORT SECTION */}
      <div className="bg-white shadow-sm rounded-2xl p-6 mb-8 border border-gray-200 flex flex-col md:flex-row justify-between items-center gap-6">
        
        {/* Upload Block */}
        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              id="axisFileInput"
              type="file"
              accept=".xlsx, .xls"
              onChange={(e) => setFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all cursor-pointer border border-gray-200 rounded-lg"
            />
          </div>
          <button
            onClick={handleUpload}
            disabled={uploading}
            className={`w-full sm:w-auto px-6 py-2.5 text-white rounded-lg text-sm font-semibold shadow-md transition-all flex items-center justify-center gap-2 ${
              uploading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg"
            }`}
          >
            {uploading ? (
              <span className="animate-pulse">Uploading...</span>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Excel
              </>
            )}
          </button>
        </div>

        {/* Export Block */}
        <div className="w-full md:w-auto">
          <button
            onClick={handleExport}
            disabled={matchedRows.length === 0}
            className={`w-full md:w-auto px-6 py-2.5 text-white rounded-lg text-sm font-semibold shadow-md transition-all flex items-center justify-center gap-2 ${
              matchedRows.length === 0 
                ? "bg-gray-300 cursor-not-allowed shadow-none" 
                : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:shadow-lg"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export ERP Matched Data
          </button>
        </div>
      </div>

      {/* MATCHED TABLE */}
      <Table
        title="Ready to Export (Matched)"
        columns={matchedCols}
        rows={matchedRows} // Passing full array, table handles pagination
        loading={loading}
        headerColor="bg-green-50"
        titleColor="text-green-800"
      />

      {/* UNMATCHED TABLE */}
      <Table
        title="Unmatched Transactions"
        columns={[...unmatchedCols, "Action"]} 
        rows={unmatchedRows} // Passing full array, table handles pagination
        loading={loading}
        headerColor="bg-red-50"
        titleColor="text-red-800"
        customRender={(row, col) => {
          // Action Button
          if (col === "Action") {
            return (
              <button
                onClick={() => handleEditVoucher(row.Id, row.VoucherNumber)}
                className="px-3 py-1.5 bg-amber-500 text-white rounded-md text-xs font-semibold hover:bg-amber-600 shadow-sm transition-colors whitespace-nowrap"
              >
                Edit Voucher
              </button>
            );
          }
          // DR/CR Badges
          if (col === "DebitCredit") {
            const isDr = row[col] === "DR";
            return (
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${isDr ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {row[col]}
              </span>
            );
          }
          // Amount Formatting
          if (col === "Amount") {
            return <span className="font-semibold text-gray-700">₹{Number(row[col]).toLocaleString('en-IN')}</span>;
          }
          // Date formatting
          if (["transactiondate", "date"].some((k) => col.toLowerCase().includes(k))) {
            return formatDate(row[col]);
          }
          return String(row[col] ?? "");
        }}
      />
    </div>
  );
}

/* =========================
   REUSABLE TABLE COMPONENT (WITH FILTER & PAGINATION)
========================= */
const Table = ({
  title,
  columns,
  rows,
  loading,
  customRender,
  headerColor = "bg-gray-100",
  titleColor = "text-gray-800"
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Reset page when search term changes or new data arrives
  useEffect(() => {
    setPage(1);
  }, [searchTerm, rows]);

  // Filter Logic
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const lower = searchTerm.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? "").toLowerCase().includes(lower)
      )
    );
  }, [rows, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const paginatedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <div className="mb-8 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      
      {/* HEADER WITH SEARCH BAR */}
      <div className={`flex flex-col sm:flex-row justify-between items-center px-6 py-4 border-b border-gray-200 gap-4 ${headerColor}`}>
        <h2 className={`text-lg font-bold ${titleColor} flex items-center gap-2`}>
          {title} 
          <span className="bg-white px-2 py-0.5 rounded-full text-sm shadow-sm border border-gray-200 text-gray-600">
            {filteredRows.length}
          </span>
        </h2>
        
        {/* Search Input */}
        <div className="relative w-full sm:w-72">
          <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by amount, voucher, details..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 flex justify-center items-center gap-3 text-gray-500">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="font-medium">Loading Data...</span>
          </div>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold tracking-wider">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="border-b px-6 py-3 whitespace-nowrap">
                    {c.replace(/([A-Z])/g, ' $1').trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center p-8 text-gray-400 font-medium">
                    <div className="flex flex-col items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {searchTerm ? "No results found for your search" : "No data available"}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRows.map((r, i) => (
                  <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                    {columns.map((c) => (
                      <td key={c} className="px-6 py-3 text-gray-600">
                        {customRender ? (
                          customRender(r, c)
                        ) : (
                          colRenderer(r, c)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION */}
      {filteredRows.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center text-sm text-gray-600">
          <span className="font-medium">
            Page <span className="text-gray-900">{page}</span> of <span className="text-gray-900">{totalPages}</span>
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white hover:shadow-sm font-medium transition-all text-gray-700 bg-white"
            >
              Previous
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white hover:shadow-sm font-medium transition-all text-gray-700 bg-white"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper for default column rendering (used by Matched Table)
const colRenderer = (row, col) => {
  if (col === "DebitCredit") {
    const isDr = row[col] === "DR";
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${isDr ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
        {row[col]}
      </span>
    );
  }
  if (col === "Amount") {
    return <span className="font-semibold text-gray-700">₹{Number(row[col]).toLocaleString('en-IN')}</span>;
  }
  if (["transactiondate", "date"].some((k) => col.toLowerCase().includes(k))) {
    return formatDate(row[col]);
  }
  return String(row[col] ?? "");
};

export default AxisBankAutomation;